/**
 * Audio playback for the training screen.
 *
 * Two playback channels:
 *
 *   - The shared `play()` channel — one clip at a time, so starting a new
 *     clip stops any previous one. The training screen chains clips through
 *     this (help button → auto-play `audio_full` on correct, etc.), and the
 *     single-channel rule is what guarantees the chain doesn't overlap
 *     itself.
 *
 *   - Per-cue playback via {@link playSuccess}, {@link playFail}, and
 *     {@link playIntro}. Each cue plays independently of the shared channel
 *     (success rings while `audio_full` plays through `play()`, etc.).
 *
 * …and two engines behind them:
 *
 *   - **HTML audio elements** — the original and still the default. One
 *     global `HTMLAudioElement` for the shared channel, a fresh one per cue
 *     (held in a module-level Set until `ended`/`error` so the GC doesn't
 *     reap it mid-playback).
 *   - **Web Audio** — used on WebKit browsers ({@link isWebKitBrowser}), i.e.
 *     Safari on an iPad, which is where the therapist app runs its speech
 *     exercises. The reason is a WebKit bug, not preference: once a
 *     `webkitSpeechRecognition` session has run, any `<audio>`/`<video>`
 *     playback leaves every later recognizer session permanently silent —
 *     it lights the mic indicator and then fires neither `onresult` nor
 *     `onerror` nor `onend` (https://bugs.webkit.org/show_bug.cgi?id=321436).
 *     The media element takes over the AVAudioSession and never gives it
 *     back to the Speech framework. Decoding the bytes ourselves and playing
 *     them through an `AudioBufferSourceNode` shares the session cleanly;
 *     a Web Audio beep is the one thing the bug reporters found harmless.
 *
 *     Web Audio needs the bytes, so the clip is `fetch`ed and decoded. That
 *     is a CORS request: bundled cues are same-origin and always work; item
 *     media is a presigned object-storage URL and works only if the bucket
 *     sends CORS headers. A refused fetch (or a decode failure, or a context
 *     the browser refuses to run) falls back to the element engine for that
 *     one play so the therapist still hears the word — at the cost of
 *     re-triggering the bug for the next recognition. Hence the warning it
 *     logs: a bucket without CORS is a configuration gap, not a code path.
 *
 *     An iOS `AudioContext` starts suspended and only runs after a user
 *     gesture. {@link installAudioUnlock} hooks the first tap anywhere in
 *     the app for that; `main.ts` installs it at boot because the routes are
 *     lazy-loaded and a listener registered from this module alone would miss
 *     every tap before the training screen's chunk arrives.
 *
 * Source URLs for the shared channel can be either:
 *   - `capacitor://localhost/...` (locally-cached file written by
 *     `lib/exercise-media-cache.ts`) — native patient build, element engine
 *   - presigned `https://` (lingodb S3) — therapist web build
 */
import successUrl from '@/assets/sound/success.mp3'
import failUrl from '@/assets/sound/fail.wav'
import introUrl from '@/assets/sound/intro.mp3'

import { isWebKitBrowser } from './platform'

// --- element engine --------------------------------------------------------

let elem: HTMLAudioElement | null = null

// Holds in-flight cue elements so GC doesn't drop them mid-playback. Each
// cue removes itself on `ended` / `error`. {@link stop} walks this set
// when the training screen tears down so a navigate-away doesn't leave a
// 1-second success cue ringing into the next screen.
const playingCues = new Set<HTMLAudioElement>()

function ensureElement(): HTMLAudioElement {
  if (elem === null) {
    elem = new Audio()
    elem.preload = 'auto'
  }
  return elem
}

function playElement(uri: string): Promise<void> {
  const el = ensureElement()
  el.pause()
  el.currentTime = 0
  el.src = uri

  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('error', onError)
      el.removeEventListener('pause', onPause)
    }
    const onEnded = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      // Surface as a console.warn but resolve instead of rejecting — a
      // failed help-button play shouldn't propagate into a Vue render error.
      console.warn('audio playback error for', uri, el.error)
      resolve()
    }
    const onPause = () => {
      // Pause fires both when the user pauses and when a new play starts
      // (we paused above). The pause-via-new-play case should resolve so
      // the previous promise unblocks cleanly.
      cleanup()
      resolve()
    }
    el.addEventListener('ended', onEnded)
    el.addEventListener('error', onError)
    el.addEventListener('pause', onPause)
    el.play().catch((e) => {
      cleanup()
      // Autoplay rejection (browser policy) — surface but don't crash.
      console.warn('audio play() rejected:', e)
      reject(e)
    })
  })
}

function stopElements(): void {
  if (elem !== null) {
    elem.pause()
    elem.currentTime = 0
    elem.removeAttribute('src')
    elem.load()
  }
  for (const cue of playingCues) {
    cue.pause()
    cue.currentTime = 0
  }
  playingCues.clear()
}

/**
 * Play a one-shot cue through a fresh HTMLAudioElement so cues can overlap
 * with the shared channel without one clobbering the other. The element is
 * pinned in {@link playingCues} until it finishes so the GC doesn't reap it
 * mid-playback.
 *
 * Resolves on `ended` / `error` / `play()` rejection — never throws.
 */
function playCueElement(url: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const el = new Audio(url)
    el.preload = 'auto'
    playingCues.add(el)
    const done = () => {
      playingCues.delete(el)
      resolve()
    }
    el.addEventListener('ended', done, { once: true })
    el.addEventListener(
      'error',
      () => {
        console.warn('cue playback error for', url, el.error)
        done()
      },
      { once: true },
    )
    el.play().catch((e) => {
      console.warn('cue play() rejected for', url, e)
      done()
    })
  })
}

// --- Web Audio engine ------------------------------------------------------

/** Whether this build plays through Web Audio at all (see the module doc). */
const webAudioEngine: boolean = isWebKitBrowser === true

type AudioContextCtor = typeof AudioContext

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as typeof window & { webkitAudioContext?: AudioContextCtor }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

let ctx: AudioContext | null = null

/** The one shared context, created on first use. `null` where Web Audio is missing. */
function ensureContext(): AudioContext | null {
  if (ctx !== null) return ctx
  const Ctor = getAudioContextCtor()
  if (Ctor === null) return null
  try {
    ctx = new Ctor()
  } catch (e) {
    console.warn('[audio] AudioContext unavailable, using audio elements:', e)
    return null
  }
  return ctx
}

/**
 * Make sure the context is running. Resumes a suspended one (allowed from a
 * gesture, and after the unlock below from anywhere). Returns `false` when the
 * browser still refuses — the caller then falls back to an element, which is
 * subject to the same autoplay policy and so fails the same way it always has.
 */
async function contextRunning(): Promise<AudioContext | null> {
  const c = ensureContext()
  if (c === null) return null
  if (c.state !== 'running') {
    try {
      await c.resume()
    } catch {
      /* fall through to the state check */
    }
  }
  return c.state === 'running' ? c : null
}

/**
 * Hook the first user gesture to start the Web Audio context. iOS keeps a
 * context suspended until `resume()` runs inside a gesture, and the classic
 * silent one-sample buffer is what makes WebKit treat the context as unlocked
 * from then on. Stays armed until the context reports `running`, so a tap
 * that lands before the context exists is not wasted.
 *
 * Cheap no-op outside the Web Audio engine. Called once, from `main.ts`.
 */
export function installAudioUnlock(
  target: Pick<Document, 'addEventListener' | 'removeEventListener'> = document,
): void {
  if (!webAudioEngine) return
  const events = ['pointerdown', 'touchend', 'keydown'] as const
  const unlock = (): void => {
    const c = ensureContext()
    if (c === null) {
      remove()
      return
    }
    try {
      const silent = c.createBuffer(1, 1, c.sampleRate)
      const source = c.createBufferSource()
      source.buffer = silent
      source.connect(c.destination)
      source.start(0)
    } catch {
      /* the resume below is the part that matters */
    }
    void c
      .resume()
      .catch(() => {})
      .then(() => {
        if (c.state === 'running') remove()
      })
  }
  const remove = (): void => {
    for (const ev of events) target.removeEventListener(ev, unlock)
  }
  for (const ev of events) target.addEventListener(ev, unlock, { passive: true })
}

/**
 * Decoded clips by URL. Cues are three fixed files; item media URLs are
 * presigned per session, so the cache is bounded and drops its oldest entry
 * rather than growing with every exercise. Failed loads are not cached — a
 * transient network error should not pin an item to the element engine.
 */
const buffers = new Map<string, Promise<AudioBuffer>>()
const BUFFER_CACHE_LIMIT = 64

async function loadBuffer(c: AudioContext, url: string): Promise<AudioBuffer> {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const bytes = await response.arrayBuffer()
  return c.decodeAudioData(bytes)
}

function cachedBuffer(c: AudioContext, url: string): Promise<AudioBuffer> {
  const hit = buffers.get(url)
  if (hit !== undefined) return hit
  const pending = loadBuffer(c, url)
  buffers.set(url, pending)
  pending.catch(() => buffers.delete(url))
  if (buffers.size > BUFFER_CACHE_LIMIT) {
    const oldest = buffers.keys().next().value
    if (oldest !== undefined) buffers.delete(oldest)
  }
  return pending
}

/** One playing `AudioBufferSourceNode`; `done` settles on end or on `stop`. */
interface Voice {
  readonly done: Promise<void>
  stop(): void
}

function startVoice(c: AudioContext, buffer: AudioBuffer): Voice {
  const source = c.createBufferSource()
  source.buffer = buffer
  source.connect(c.destination)
  let settle: () => void = () => {}
  const done = new Promise<void>((resolve) => {
    settle = resolve
  })
  // `ended` fires for a natural end and for `stop()` alike — one settle path.
  source.onended = () => {
    source.disconnect()
    settle()
  }
  source.start(0)
  return {
    done,
    stop(): void {
      try {
        source.stop(0)
      } catch {
        // Already ended — `onended` has settled or is about to.
      }
    },
  }
}

let sharedVoice: Voice | null = null
/**
 * Bumped by every `play()` and `stop()`. Loading a clip is asynchronous, so a
 * play that was superseded while its bytes were still arriving must not start
 * late on top of its successor: it checks the generation after the await.
 */
let sharedGeneration = 0
const cueVoices = new Set<Voice>()

function stopWebAudio(): void {
  sharedGeneration++
  sharedVoice?.stop()
  sharedVoice = null
  for (const voice of cueVoices) voice.stop()
  cueVoices.clear()
}

/**
 * Shared-channel play through Web Audio. Resolves `false` when this engine
 * could not take the clip (no running context, fetch refused, undecodable),
 * leaving the caller to use an element; `true` once playback has ended or
 * been superseded.
 */
async function playWebAudio(uri: string): Promise<boolean> {
  const generation = ++sharedGeneration
  sharedVoice?.stop()
  sharedVoice = null
  for (const voice of cueVoices) voice.stop()
  cueVoices.clear()

  const c = await contextRunning()
  if (c === null) return false
  let buffer: AudioBuffer
  try {
    buffer = await cachedBuffer(c, uri)
  } catch (e) {
    warnFallback(uri, e)
    return false
  }
  // Superseded while loading (another play, or stop()) — nothing to do; the
  // generation owner has already resolved us by moving on.
  if (generation !== sharedGeneration) return true

  const voice = startVoice(c, buffer)
  sharedVoice = voice
  await voice.done
  if (sharedVoice === voice) sharedVoice = null
  return true
}

async function playCueWebAudio(url: string): Promise<boolean> {
  const c = await contextRunning()
  if (c === null) return false
  let buffer: AudioBuffer
  try {
    buffer = await cachedBuffer(c, url)
  } catch (e) {
    warnFallback(url, e)
    return false
  }
  const voice = startVoice(c, buffer)
  cueVoices.add(voice)
  await voice.done
  cueVoices.delete(voice)
  return true
}

const warnedOrigins = new Set<string>()

/**
 * Once per origin: a refused fetch is almost always a bucket without CORS
 * headers, which someone has to fix in the bucket, so say so loudly once
 * rather than on every help tap.
 */
function warnFallback(url: string, error: unknown): void {
  let origin = url
  try {
    origin = new URL(url, location.href).origin
  } catch {
    /* keep the raw url */
  }
  if (warnedOrigins.has(origin)) return
  warnedOrigins.add(origin)
  console.warn(
    `[audio] could not play ${origin} through Web Audio, falling back to an <audio> element ` +
      '(on iPadOS Safari that breaks speech recognition — if this is object storage, ' +
      'it needs CORS headers):',
    error,
  )
}

// --- public API ------------------------------------------------------------

/**
 * Play a single clip on the shared channel. Stops any clip already
 * playing. Returns a promise that resolves when the clip finishes (or is
 * interrupted by another `play`); rejects only on unrecoverable error.
 *
 * Tolerant of `null` / empty URIs — the training screen calls this even
 * for help buttons whose backing media is missing for a particular item.
 * In that case the promise resolves immediately with no playback.
 */
export async function play(uri: string | null): Promise<void> {
  if (uri === null || uri === '') return
  if (webAudioEngine) {
    stopElements()
    if (await playWebAudio(uri)) return
  }
  return playElement(uri)
}

/**
 * Stop the shared channel and every in-flight cue. Safe to call
 * unconditionally — used by TrainingView in `onBeforeUnmount` and after
 * each item advance so a half-played cue doesn't bleed into the next
 * item.
 */
export function stop(): void {
  if (webAudioEngine) stopWebAudio()
  stopElements()
}

/**
 * Play a one-shot cue clip, independent of the shared channel.
 * Resolves when the cue finishes — never throws.
 */
async function playCue(url: string): Promise<void> {
  if (webAudioEngine && (await playCueWebAudio(url))) return
  return playCueElement(url)
}

/**
 * Success cue played after a correct speech-recognition match or a
 * self-assessment ✓. Resolves when the cue finishes so callers can
 * sequence follow-up audio cleanly.
 */
export function playSuccess(): Promise<void> {
  return playCue(successUrl)
}

/**
 * Failure cue played whenever the patient produces a wrong / unmatched
 * answer (speech-recognition miss, self-assessment ✗, or the third-miss
 * auto-reveal). Resolves when the cue finishes.
 */
export function playFail(): Promise<void> {
  return playCue(failUrl)
}

/**
 * Welcome cue played once at the start of an exercise (when the first
 * item becomes visible) and again when resuming a paused exercise. The
 * caller is responsible for the "once per entry" gating — the helper
 * just plays the cue.
 */
export function playIntro(): Promise<void> {
  return playCue(introUrl)
}
